import { useAppForm } from '@massilia/features'
import { flightSearchEnum } from '@massilia/features'
import { METROPOLITAN_FRANCE_IATA_CODES, REUNION_IATA_CODE } from '@massilia/queries'
import { Button, cn } from '@massilia/ui'
import { standardSchemaValidators } from '@tanstack/react-form'
import { add, addDays, format, isBefore, startOfDay } from 'date-fns'
import { Minus, Plus, Search } from 'lucide-react'
import { useState } from 'react'

import type { FlightSearchSchemaType } from '@/lib/schemas'

import {
  DEFAULT_FLIGHT_LET,
  DEFAULT_OPTION,
  DEFAULT_VALUES,
  flightSearchSchema,
  travelClassTypesLabels,
} from '@/lib/schemas'

import { FlightDateWarningModal } from '../flight-date-warning-modal'
import { RoundTripContinuityModal } from '../round-trip-continuity-modal'
import { FlightLegRowFormGroup } from './flight-leg-row-form-group'
import { PassengersFormGroup } from './passengers-form-group'

const WIDGET_BASE_ORIGIN_KEY = '__MASSILIA_WIDGET_BASE_ORIGIN__'

interface FlightSearchFormProps {
  errorPopoverSide?: 'bottom' | 'top'
  errorsDisplayType?: 'bellow' | 'popover'
  initialValues?: FlightSearchSchemaType
}

export function FlightSearchForm({
  errorPopoverSide = 'bottom',
  errorsDisplayType = 'popover',
  initialValues,
}: FlightSearchFormProps) {
  const [isContinuityModalOpen, setIsContinuityModalOpen] = useState(false)
  const [isDateWarningModalOpen, setIsDateWarningModalOpen] = useState(false)

  const form = useAppForm({
    defaultValues: initialValues ?? DEFAULT_VALUES,
    listeners: {
      onChange: ({ fieldApi, formApi }) => {
        if (fieldApi.name === 'type') {
          const newType = formApi.getFieldValue('type')
          const flights = formApi.getFieldValue('flight')

          if (['one-way', 'round-trip'].includes(newType)) {
            formApi.setFieldValue(
              'flight',
              flights.slice(0, 1).map((f) => ({
                ...f,
                date: { from: newType === 'one-way' ? new Date() : undefined },
              }))
            )
          } else {
            formApi.setFieldValue('flight', [
              ...[{ ...flights.at(-1)!, date: { from: new Date() } }],
              {
                date: {
                  from: add(flights.at(-1)?.date.from ?? new Date(), { days: 1 }),
                },
                departure: DEFAULT_OPTION,
                destination: DEFAULT_OPTION,
              },
            ])
          }
          //NOTE: Resets errors as validation paths aren't the same on every type
          formApi.setErrorMap({
            onSubmit: {
              fields: {},
            },
          })
        }
      },
    },
    onSubmit: ({ value }) => {
      const serializedValue = {
        flight: value.flight.map((flight) => ({
          date: {
            from: flight.date.from ? format(flight.date.from, 'yyyy-MM-dd') : undefined,
            to: flight.date.to ? format(flight.date.to, 'yyyy-MM-dd') : undefined,
          },
          departure: {
            category: flight.departure.category,
            label: flight.departure.label,
            value: flight.departure.value,
          },
          destination: {
            category: flight.destination.category,
            label: flight.destination.label,
            value: flight.destination.value,
          },
        })),
        passengers: {
          adults: value.passengers.adults,
          babies: value.passengers.babies,
          infants: value.passengers.infants,
          seniors: value.passengers.seniors,
          youngsters: value.passengers.youngsters,
        },
        travelClass: value.travelClass,
        type: value.type,
        withLuggage: value.withLuggage,
      }

      const widgetBaseOrigin =
        (window as Window & { [WIDGET_BASE_ORIGIN_KEY]?: string })[WIDGET_BASE_ORIGIN_KEY] ??
        window.location.origin
      const resultsUrl = new URL('/flights/results', widgetBaseOrigin)
      resultsUrl.searchParams.set('flightSearch', JSON.stringify(serializedValue))
      window.location.assign(resultsUrl.toString())
    },
    validators: {
      onSubmit: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, flightSearchSchema),
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()

        const values = form.state.values
        const departureDate = values.flight[0]?.date?.from

        // Check 1: Departure within 5 days — blocking modal
        if (departureDate && isBefore(departureDate, addDays(startOfDay(new Date()), 5))) {
          setIsDateWarningModalOpen(true)
          return
        }

        // Check 2: Round-trip with Réunion — territorial continuity modal
        if (values.type === 'round-trip' && values.travelClass === 'ECONOMY') {
          const hasReunionToMetropolitanFranceSegment = values.flight.some(
            (leg) =>
              leg.departure.value === REUNION_IATA_CODE &&
              METROPOLITAN_FRANCE_IATA_CODES.has(leg.destination.value)
          )
          if (hasReunionToMetropolitanFranceSegment) {
            setIsContinuityModalOpen(true)
            return
          }
        }

        void form.handleSubmit()
      }}
    >
      <div className="flex flex-1 flex-col gap-4">
        {/* Type of search selection */}
        <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-6">
          <form.AppField
            children={(field) => (
              <field.RadioGroupField
                options={Object.entries(flightSearchEnum).map(([key, value]) => ({
                  label: value,
                  value: key,
                }))}
                orientation="horizontal"
              />
            )}
            name="type"
          />
          <div className="flex flex-row gap-2">
            <form.AppField name="passengers">
              {() => (
                <PassengersFormGroup
                  fields={{
                    adults: 'passengers.adults',
                    babies: 'passengers.babies',
                    infants: 'passengers.infants',
                    seniors: 'passengers.seniors',
                    youngsters: 'passengers.youngsters',
                  }}
                  form={form}
                />
              )}
            </form.AppField>

            <form.AppField name="travelClass">
              {(field) => (
                <field.SelectField
                  options={Object.entries(travelClassTypesLabels).map(([key, value]) => ({
                    label: value,
                    value: key,
                  }))}
                />
              )}
            </form.AppField>
          </div>
        </div>

        <div className={cn('flex flex-col flex-wrap items-center gap-3')}>
          <form.Subscribe
            selector={(state) => state.values.type}
            children={(type) => (
              <>
                <form.AppField
                  mode="array"
                  name="flight"
                  children={(field) => {
                    return (
                      <>
                        {field.state.value.map((_, i) => (
                          <div className="flex w-full flex-row gap-1" key={`flight-leg-${i}`}>
                            <FlightLegRowFormGroup
                              dateType={type === 'round-trip' ? 'range' : 'single'}
                              disabledDateFn={(date) => {
                                const today = new Date()
                                const previousDate = field.form.getFieldValue(
                                  `flight[${i - 1}].date.from`
                                )
                                const isDateDisabled =
                                  (previousDate && date < previousDate) || date < today

                                return isDateDisabled
                              }}
                              fields={{
                                date: `flight[${i}].date`,
                                departure: `flight[${i}].departure`,
                                destination: `flight[${i}].destination`,
                              }}
                              form={form}
                              errorPopoverSide={errorPopoverSide}
                              errorsDisplayType={errorsDisplayType}
                              key={i}
                            />
                            {type === 'multi-destination' && i >= 2 && (
                              <Button
                                hidden={form.state.values.type !== 'multi-destination'}
                                onClick={() => {
                                  field.removeValue(i)
                                }}
                                type="button"
                                variant="outline"
                              >
                                <Minus className="h-5 w-5" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </>
                    )
                  }}
                />
                <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    className={cn('transition-all duration-200', {
                      'opacity-0': form.state.values.type !== 'multi-destination',
                    })}
                    onClick={() => {
                      if (form.state.values.type === 'multi-destination') {
                        form.pushFieldValue('flight', {
                          ...DEFAULT_FLIGHT_LET,
                          date: {
                            from: add(form.state.values.flight.at(-1)?.date.from ?? new Date(), {
                              days: 1,
                            }),
                          },
                        })
                      }
                    }}
                    type="button"
                  >
                    <Plus className="h-5 w-5" />
                    <p className="text-sm font-medium">Ajouter un vol</p>
                  </Button>

                  <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                    <form.AppField
                      children={(field) => (
                        <field.SwitchField
                          containerClassName="flex-row-reverse items-center gap-2"
                          label="Bagages en soute inclus"
                        />
                      )}
                      name="withLuggage"
                    />

                    <Button
                      className="bg-brand-600 hover:bg-brand-800 w-full justify-center text-white sm:w-fit"
                      type="submit"
                    >
                      <Search className="size-4" />
                      Rechercher
                    </Button>
                  </div>
                </div>
              </>
            )}
          />
        </div>
      </div>

      <RoundTripContinuityModal
        isModalOpen={isContinuityModalOpen}
        onSubmit={() => void form.handleSubmit()}
        setIsModalOpen={setIsContinuityModalOpen}
      />
      <FlightDateWarningModal
        isModalOpen={isDateWarningModalOpen}
        setIsModalOpen={setIsDateWarningModalOpen}
      />
    </form>
  )
}
